/* ==========================================================================
   Serinia – globales Script (jede Seite, mit defer geladen)
   - Geldformat wie Liquid money_without_trailing_zeros
   - Fenster (<dialog>): öffnen, schliessen mit Ausfahren, Esc, Rand
   - Warenkorb: Cart AJAX API + Section Rendering API (Drawer rendert Shopify)
   - Suche: Script wird erst beim ersten Öffnen geladen (sr-search.js)
   - leichte Bewegung, Formular-Hinweise, wischbare Karten
   Ohne dieses Script funktioniert alles weiter über normale Links/Formulare.
   ========================================================================== */
(() => {
  const S = window.Serinia || (window.Serinia = {});
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');

  /* --- Geld ------------------------------------------------------------- */
  // Rappen -> "CHF 144.80", ganze Franken ohne ".00", geschütztes Leerzeichen
  S.money = (cents) => {
    const value = Number(cents) / 100;
    const group = (n, decimals, thousands, decimal) => {
      const [int, frac] = n.toFixed(decimals).split('.');
      const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return frac ? grouped + decimal + frac : grouped;
    };
    const format = (S.moneyFormat || 'CHF {{amount}}').replace(/<[^>]*>/g, '');
    const text = format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return group(value, 0, ',', '.');
        case 'amount_with_comma_separator': return group(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return group(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return group(value, 2, '’', '.');
        case 'amount_with_space_separator': return group(value, 2, ' ', ',');
        default: return group(value, 2, ',', '.');
      }
    });
    return text.replace(/([.,])00(?!\d)/, '').replace(/ /g, ' ');
  };

  /* --- Fenster ------------------------------------------------------------ */
  S.openDialog = (dialog) => {
    if (!dialog || dialog.open) return;
    dialog.classList.remove('is-closing');
    dialog.showModal();
    dialog.focus();
    dialog.dispatchEvent(new CustomEvent('sr:open'));
  };
  S.closeDialog = (dialog) => {
    if (!dialog?.open || dialog.classList.contains('is-closing')) return;
    const done = () => {
      dialog.classList.remove('is-closing');
      dialog.close();
    };
    // Ausfahren nur, wenn das Fenster eine eigene Schliess-Animation hat
    const before = getComputedStyle(dialog).animationName;
    dialog.classList.add('is-closing');
    const after = getComputedStyle(dialog).animationName;
    if (reduced.matches || after === before || after === 'none') return done();
    dialog.addEventListener('animationend', done, { once: true });
    setTimeout(done, 400); // falls der Browser die Animation auslässt
  };

  let searchLoaded = null;
  const loadSearch = () => {
    searchLoaded ||= import(S.assets.search).catch(() => { searchLoaded = null; });
    return searchLoaded;
  };

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-sr-open]');
    if (opener) {
      const dialog = document.getElementById(opener.dataset.srOpen);
      if (!dialog) return; // kein Fenster: normaler Link (z. B. Suche-Seite)
      event.preventDefault();
      S.openDialog(dialog);
      if (dialog.id === 'sr-search') loadSearch();
      return;
    }
    const closer = event.target.closest('[data-sr-close]');
    const parent = closer?.closest('dialog');
    if (parent) {
      event.preventDefault();
      S.closeDialog(parent);
      return;
    }
    // Tippen auf den abgedunkelten Rand ausserhalb der Fensterfläche
    if (event.target instanceof HTMLDialogElement && event.target.open) {
      const r = event.target.getBoundingClientRect();
      const inside = event.clientX >= r.left && event.clientX <= r.right
        && event.clientY >= r.top && event.clientY <= r.bottom;
      if (!inside) S.closeDialog(event.target);
    }
  });
  // Esc: mit Ausfahr-Animation statt sofort
  document.addEventListener('cancel', (event) => {
    if (!(event.target instanceof HTMLDialogElement)) return;
    event.preventDefault();
    S.closeDialog(event.target);
  }, true);
  // Suche vorladen, sobald jemand in Richtung Lupe zielt
  document.addEventListener('pointerenter', (event) => {
    if (event.target.closest?.('[data-sr-open="sr-search"]')) loadSearch();
  }, true);

  /* --- Warenkorb ---------------------------------------------------------- */
  const cart = {
    roots: () => [...document.querySelectorAll('[data-sr-cart-root]')],
    sectionIds() {
      return this.roots().map((root) => root.dataset.sectionId || 'sr-cart-drawer');
    },
    drawer: () => document.getElementById('sr-cart'),

    // Antwort der Section Rendering API einsetzen. Nur der Inhalt wird
    // ersetzt – das <dialog> bleibt offen; Fokus bleibt am gleichen Ort.
    render(sections) {
      if (!sections) return;
      const focused = document.activeElement;
      const focusedLabel = focused?.getAttribute('aria-label');
      let count = null;
      this.roots().forEach((root) => {
        const html = sections[root.dataset.sectionId || 'sr-cart-drawer'];
        if (!html) return;
        const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-sr-cart-inner]');
        const current = root.querySelector('[data-sr-cart-inner]');
        if (!fresh || !current) return;
        current.replaceWith(fresh);
        count = fresh.dataset.count;
        // Fokus lag auf einem ersetzten Element (z. B. Plus): gleiches wieder fokussieren
        if (focused && !document.contains(focused) && root.contains(fresh)) {
          const again = focusedLabel && [...root.querySelectorAll('[aria-label]')].find((el) => el.getAttribute('aria-label') === focusedLabel);
          (again || root.closest('dialog') || root).focus?.();
        }
      });
      if (count !== null) this.setCount(Number(count));
    },

    setCount(n) {
      document.querySelectorAll('[data-sr-cart-count]').forEach((el) => {
        if (el.textContent === `(${n})`) return;
        el.textContent = `(${n})`;
        el.closest('a')?.setAttribute('aria-label', (el.closest('a').getAttribute('aria-label') || '').replace(/\d+/, n));
        el.classList.remove('is-bumped');
        void el.offsetWidth; // Animation neu starten
        el.classList.add('is-bumped');
      });
    },

    async request(url, body) {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...body, sections: this.sectionIds(), sections_url: location.pathname }),
      });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || data.message || S.strings.error);
      return data;
    },

    // items: [{ id, quantity, properties }] – open: Drawer öffnen (Upsell: nein)
    async add(items, { open = true } = {}) {
      const data = await this.request(`${S.routes.cartAdd}.js`, { items });
      this.render(data.sections);
      if (open) S.openDialog(this.drawer());
      document.dispatchEvent(new CustomEvent('sr:cart-changed', { detail: { added: data.items } }));
      return data;
    },

    async change(key, quantity) {
      const data = await this.request(`${S.routes.cartChange}.js`, { id: key, quantity });
      this.render(data.sections);
      document.dispatchEvent(new CustomEvent('sr:cart-changed', { detail: { cart: data } }));
      return data;
    },

    // mehrere Zeilen auf einmal, z. B. ein ganzes Set entfernen: { key: 0, … }
    async update(updates) {
      const data = await this.request(`${S.routes.cartUpdate}.js`, { updates });
      this.render(data.sections);
      document.dispatchEvent(new CustomEvent('sr:cart-changed', { detail: { cart: data } }));
      return data;
    },
  };
  S.cart = cart;

  // Stiller Hinweis unter dem Header, z. B. nach «Hinzufügen» ohne Drawer:
  // "■ Im Warenkorb / Serinia Pyjama Set · Off White, M   Ansehen" – blendet nach 3.5 s aus.
  let noteTimer;
  S.notifyAdded = (text) => {
    let note = document.querySelector('.sr-added');
    if (!note) {
      note = document.createElement('div');
      note.className = 'sr-added';
      note.setAttribute('role', 'status');
      note.innerHTML = '<p><span class="sr-added__title"></span><span class="sr-added__text"></span></p><button class="sr-link" type="button"></button>';
      note.querySelector('button').addEventListener('click', () => {
        note.classList.remove('is-visible');
        S.openDialog(cart.drawer());
      });
      note.addEventListener('mouseenter', () => clearTimeout(noteTimer));
      note.addEventListener('mouseleave', () => { noteTimer = setTimeout(() => note.classList.remove('is-visible'), 1500); });
      document.body.append(note);
    }
    note.querySelector('.sr-added__title').textContent = S.strings.inCart;
    note.querySelector('.sr-added__text').textContent = text;
    note.querySelector('button').textContent = S.strings.view;
    note.classList.remove('is-visible');
    void note.offsetWidth; // Einblenden neu starten
    note.classList.add('is-visible');
    clearTimeout(noteTimer);
    noteTimer = setTimeout(() => note.classList.remove('is-visible'), 3500);
  };

  document.addEventListener('click', async (event) => {
    const openLink = event.target.closest('[data-sr-cart-open]');
    if (openLink && cart.drawer() && !document.querySelector('.sr-cart-page')) {
      event.preventDefault();
      S.openDialog(cart.drawer());
      return;
    }
    const change = event.target.closest('[data-sr-cart-change]');
    if (!change) return;
    event.preventDefault();
    const inner = change.closest('[data-sr-cart-inner]');
    inner?.setAttribute('aria-busy', 'true');
    try {
      await cart.change(change.dataset.srCartChange, Number(change.dataset.quantity));
    } catch (error) {
      inner?.removeAttribute('aria-busy');
      const note = inner?.querySelector('[data-sr-cart-error]');
      if (note) {
        note.textContent = error.message;
        note.hidden = false;
      }
    }
  });

  // Set als Ganzes entfernen (Formular funktioniert auch ohne JavaScript)
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-sr-cart-update]');
    if (!form) return;
    event.preventDefault();
    const updates = {};
    form.querySelectorAll('input[name^="updates["]').forEach((field) => {
      updates[field.name.slice(8, -1)] = Number(field.value);
    });
    const inner = form.closest('[data-sr-cart-inner]');
    inner?.setAttribute('aria-busy', 'true');
    try {
      await cart.update(updates);
    } catch (error) {
      inner?.removeAttribute('aria-busy');
      const note = inner?.querySelector('[data-sr-cart-error]');
      if (note) {
        note.textContent = error.message;
        note.hidden = false;
      }
    }
  });

  /* --- Formulare: Hinweis unter dem Feld statt Browser-Blase ------------- */
  const isMail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.trim());
  const showNote = (field, text) => {
    const anchor = field.closest('.sr-footer__field') || field;
    let note = anchor.nextElementSibling;
    if (!note?.classList.contains('sr-note')) {
      note = document.createElement('p');
      note.className = 'sr-note';
      note.setAttribute('role', 'alert');
      note.id = `${field.id}-note`;
      anchor.after(note);
    }
    note.textContent = text;
    field.setAttribute('aria-invalid', 'true');
    field.setAttribute('aria-describedby', note.id);
  };
  const clearNote = (field) => {
    if (field.getAttribute('aria-invalid') !== 'true') return;
    field.removeAttribute('aria-invalid');
    const anchor = field.closest('.sr-footer__field') || field;
    if (anchor.nextElementSibling?.classList.contains('sr-note')) anchor.nextElementSibling.remove();
  };
  const initForms = (scope = document) => {
    scope.querySelectorAll('.sr-footer__form, .sr-form').forEach((form) => {
      if (form.dataset.srReady) return;
      form.dataset.srReady = '';
      form.noValidate = true;
      form.addEventListener('submit', (event) => {
        let first = null;
        form.querySelectorAll('input[type="email"], textarea[required]').forEach((field) => {
          const ok = field.type === 'email' ? isMail(field.value) : field.value.trim().length > 0;
          if (ok) return clearNote(field);
          showNote(field, field.type === 'email' ? S.strings.invalidEmail : S.strings.missingMessage);
          first ||= field;
        });
        if (first) {
          event.preventDefault();
          first.focus();
        }
      });
      form.addEventListener('input', (event) => clearNote(event.target));
    });
  };

  /* --- Newsletter im Footer: direkt an Klaviyo (Willkommens-Flow) --------
     Capture-Phase: vor Shopifys Bot-Schutz, der Kundenformulare sonst
     abfängt. Ungültige Adressen laufen weiter in die Prüfung oben.
     Antwortet Klaviyo nicht, geht das Formular wie ohne JavaScript an Shopify. */
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest?.('form[data-sr-klaviyo]');
    if (!form || !form.dataset.srKlaviyo) return;
    const field = form.querySelector('input[type="email"]');
    if (!field || !isMail(field.value)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const [companyId, listId] = form.dataset.srKlaviyo.split(':');
    const button = form.querySelector('[type="submit"]');
    if (button?.disabled) return;
    if (button) button.disabled = true;
    try {
      const response = await fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(companyId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/vnd.api+json', accept: 'application/vnd.api+json', revision: '2026-07-15' },
        body: JSON.stringify({ data: { type: 'subscription', attributes: {
          custom_source: 'Serinia Theme 2026 Footer',
          profile: { data: { type: 'profile', attributes: {
            email: field.value.trim(),
            subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
            properties: { serinia_signup_source: 'theme_2026_footer' },
          } } },
        }, relationships: { list: { data: { type: 'list', id: listId } } } } }),
      });
      if (response.status !== 202) throw new Error(String(response.status));
      const done = document.createElement('p');
      done.className = 'sr-done';
      done.setAttribute('role', 'status');
      done.textContent = S.strings.subscribed;
      form.replaceChildren(done);
    } catch (_) {
      if (button) button.disabled = false;
      HTMLFormElement.prototype.submit.call(form); // Shopify-Formular als Rückfall
    }
  }, true);

  /* --- Wischbare Karten (Bewertungen): Balken + Zähler ------------------- */
  class SrCards extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('.sr-cards');
      this.bar = this.querySelector('.sr-cards__bar');
      this.count = this.querySelector('.sr-cards__count');
      if (!this.list || !this.bar) return;
      this.update = this.update.bind(this);
      this.list.addEventListener('scroll', this.update, { passive: true });
      window.addEventListener('resize', this.update);
      this.update();
    }
    disconnectedCallback() {
      window.removeEventListener('resize', this.update);
    }
    update() {
      const total = this.list.querySelectorAll('.sr-card').length || 1;
      const max = this.list.scrollWidth - this.list.clientWidth;
      const index = Math.round((max > 0 ? this.list.scrollLeft / max : 0) * (total - 1));
      this.bar.style.transform = `scaleX(${(index + 1) / total})`;
      if (this.count) this.count.textContent = `${index + 1}/${total}`;
    }
  }
  customElements.define('sr-cards', SrCards);

  /* --- Bewegung (bei "Bewegung reduzieren" aus) -------------------------- */
  // Bilder blenden ein, Abschnitte erscheinen einmalig beim Scrollen
  const revealObserver = 'IntersectionObserver' in window && new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-revealed');
      revealObserver.unobserve(entry.target);
    });
  }, { rootMargin: '0px 0px -8% 0px' });

  S.initMotion = (scope = document) => {
    if (reduced.matches) return;
    scope.querySelectorAll('.sr-main img:not(.sr-fade):not(.sr-card__hover)').forEach((img) => {
      if (img.complete && img.naturalWidth) return;
      img.classList.add('sr-fade');
      const done = () => img.classList.add('is-loaded');
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });
    });
    if (!revealObserver) return;
    scope.querySelectorAll('[data-sr-reveal]:not(.sr-reveal)').forEach((el) => {
      if (el.getBoundingClientRect().top < window.innerHeight) return; // schon sichtbar
      el.classList.add('sr-reveal');
      revealObserver.observe(el);
    });
  };

  // Akkordeons und Auswahlzeilen gleiten auf und zu. "Immer nur eine offen"
  // (name="…") übernimmt das Script, damit auch das Schliessen gleitet.
  const slide = (details, opening) => {
    const content = details.querySelector(':scope > summary + *');
    if (!content || reduced.matches) {
      details.open = opening;
      return;
    }
    if (opening) details.open = true;
    const height = content.scrollHeight;
    content.style.overflow = 'hidden';
    const animation = content.animate(
      opening
        ? [{ height: '0px', opacity: 0 }, { height: `${height}px`, opacity: 1 }]
        : [{ height: `${height}px`, opacity: 1 }, { height: '0px', opacity: 0 }],
      { duration: 260, easing: 'cubic-bezier(.2,.7,.3,1)' },
    );
    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      animation.cancel();
      if (!opening) details.open = false;
      content.style.overflow = '';
    };
    animation.onfinish = finish;
    setTimeout(finish, 320);
  };
  S.toggleDetails = (details, opening = !details.open) => {
    const group = details.dataset.srGroup || details.getAttribute('name');
    if (group) {
      document.querySelectorAll(`details[name="${CSS.escape(group)}"], details[data-sr-group="${CSS.escape(group)}"]`).forEach((other) => {
        other.removeAttribute('name');
        other.dataset.srGroup = group;
        if (opening && other !== details && other.open) slide(other, false);
      });
    }
    slide(details, opening);
  };
  document.addEventListener('click', (event) => {
    const summary = event.target.closest('details[data-sr-details] > summary');
    if (!summary) return;
    event.preventDefault();
    S.toggleDetails(summary.parentElement);
  });

  /* --- Start ------------------------------------------------------------- */
  const init = (scope) => {
    initForms(scope);
    S.initMotion(scope);
  };
  init(document);
  // Theme-Editor: neu gerenderte Sections ebenfalls einrichten
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
